import React, { useState, useEffect } from 'react';
import { Building2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { authService } from '../services/auth.service';
import { Campus } from '../types/auth.types';
import Loading from '../components/common/Loading';

const LoginPage: React.FC = () => {
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [selectedCampus, setSelectedCampus] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    fetchCampuses();
  }, []);

  const fetchCampuses = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await authService.getAllCampuses();

      if (Array.isArray(data)) {
        setCampuses(data);
        if (data.length > 0) {
          setSelectedCampus(data[0]._id);
        }
      } else {
        console.error('Invalid campuses data:', data);
        setCampuses([]);
        setError('Dữ liệu campus không hợp lệ. Vui lòng kiểm tra backend.');
      }
    } catch (err: any) {
      console.error('Error fetching campuses:', err);
      setCampuses([]);
      setError(err?.message || 'Không thể tải danh sách campus. Vui lòng kiểm tra backend đã chạy chưa.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!selectedCampus) {
      setError('Vui lòng chọn campus trước khi đăng nhập');
      return;
    }
    authService.loginWithGoogle(selectedCampus);
  };

  const handleForgotPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail || !resetEmail.includes('@')) {
      setError('Vui lòng nhập email hợp lệ');
      return;
    }
    setResetSuccess(true);
    setError('');
    setTimeout(() => {
      setShowForgotPassword(false);
      setResetSuccess(false);
      setResetEmail('');
    }, 3000);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0066cc] to-[#ff6b00] flex items-center justify-center p-4">
        <Loading />
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0066cc] to-[#ff6b00] flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-[#ff6b00] p-4 rounded-2xl">
                <Building2 className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-bold mb-2">Đặt lại mật khẩu</h1>
            <p className="text-gray-600">Nhập email để nhận hướng dẫn đặt lại mật khẩu</p>
          </div>

          {resetSuccess && (
            <Alert className="mb-6 bg-green-50 border-green-200">
              <AlertDescription className="text-green-800">
                Hướng dẫn đặt lại mật khẩu đã được gửi đến email của bạn.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleForgotPassword} className="space-y-6">
            <div>
              <Label htmlFor="reset-email">Địa chỉ Email</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="email.cua.ban@fpt.edu.vn"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="mt-2 border-gray-300"
              />
            </div>

            <Button type="submit" className="w-full bg-[#ff6b00] hover:bg-[#e56000]">
              Gửi link đặt lại
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setShowForgotPassword(false);
                setError('');
                setResetEmail('');
              }}
            >
              Quay lại đăng nhập
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0066cc] to-[#ff6b00] flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-2xl">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-[#ff6b00] p-4 rounded-2xl">
              <Building2 className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold mb-2">Quản lý Phòng học IoT</h1>
          <p className="text-gray-600">Đăng nhập vào tài khoản của bạn</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form className="space-y-6">
          <div>
            <Label htmlFor="campus">Chọn Campus</Label>
            <div className="relative mt-2">
              <select
                id="campus"
                value={selectedCampus}
                onChange={(e) => setSelectedCampus(e.target.value)}
                className="block w-full px-4 py-3 pr-10 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff6b00] focus:border-transparent bg-white shadow-sm transition-all appearance-none"
              >
                <option value="">-- Chọn campus --</option>
                {Array.isArray(campuses) &&
                  campuses.map((campus) => (
                    <option key={campus._id} value={campus._id}>
                      {campus.campusName} ({campus.campusCode})
                    </option>
                  ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Vui lòng chọn campus trước khi đăng nhập
            </p>
          </div>

          <Button
            type="button"
            onClick={handleGoogleLogin}
            disabled={!selectedCampus}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-300 shadow-md hover:shadow-lg transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Đăng nhập với Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">hoặc liên hệ</span>
            </div>
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-gray-600">
              <span className="font-medium">Phòng Đào tạo</span> để được hỗ trợ tài khoản
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-[#0066cc]">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <a href="mailto:training@fpt.edu.vn" className="hover:underline">
                training@fpt.edu.vn
              </a>
            </div>
          </div>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>© 2024 FPT University</p>
          <p className="mt-1">Powered by IoT & Smart Technologies</p>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;
